import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { ActivatedRoute, Router } from '@angular/router';
import { fadeInOut } from '@x/common/animations';
import { RecentSearchesService } from '@x/ecommerce-shop/app/core/search/services/recent-searches.service';
import { ShopProductSearchService } from '@x/ecommerce/shop-client';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  of,
  retry,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { SearchUiStateService } from '../services/search-ui-state.service';

@Component({
  selector: 'x-search-input',
  templateUrl: './search-input.component.html',
  styleUrls: ['./search-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'x-search-input' },
  animations: [fadeInOut],
})
export class SearchInputComponent implements OnInit, OnChanges, OnDestroy {
  private _subscription = new Subscription();

  @Output() closeOverlay = new EventEmitter();

  @Output() dropDownOpened = new EventEmitter<boolean>();

  @HostBinding('@fadeInOut')
  fadeInAnimate: string;

  searching$ = new BehaviorSubject<boolean>(false);

  searchControl = new FormControl<string>('', { nonNullable: true });

  group = new FormGroup({
    search: this.searchControl,
  });

  @Input() set searchValue(val: string | null) {
    if (!val) return;
    this.searchControl.setValue(val, { emitEvent: false });
  }

  @Input() searchFocus: boolean = true;

  showClearButton$: Observable<boolean>;

  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger: MatAutocompleteTrigger;
  @ViewChild('searchInput') searchInput: ElementRef;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private shopProductSearchService: ShopProductSearchService,
    private recentSearchService: RecentSearchesService,
    private searchUiStateService: SearchUiStateService,
  ) {}

  performSearch(): void {
    const { channelCode } = this.route.snapshot.params;

    if (this.searchControl.valid && channelCode) {
      const value = this.searchControl.value;
      this.router.navigate([`/${channelCode}/ps`, value]);
      this.closeOverlay.emit();
    }
  }

  recentSearches$ = this.searchControl.valueChanges.pipe(
    startWith(this.searchControl.value), // need this to populate recentSearches initially
    debounceTime(400),
    switchMap((value) =>
      of(this.recentSearchService.getRecentSearches()).pipe(
        map((items) => {
          if (!value) return items;

          return items?.filter((item) => {
            return item.searchText?.includes(value) ? item : null;
          });
        }),
      ),
    ),
  );

  searchAutoComplete$ = this.searchControl.valueChanges.pipe(
    debounceTime(400),
    distinctUntilChanged(), // Only if value change
    filter((value) => value.trim().length > 0), // Non-empty string
    tap(() => this.searching$.next(true)),
    switchMap((value) => {
      return this.shopProductSearchService
        .fetchProductSearchAutoComplete({ searchText: value })
        .pipe(
          map((results) => {
            return results
              .filter((result) => {
                const recentSearches = this.recentSearchService.getRecentSearches() || [];
                const isQueryRecent = recentSearches.some((recent) =>
                  result.query.startsWith(recent.searchText),
                );
                return !isQueryRecent ? result.query : null;
              })
              .map((filteredResult) => filteredResult.query);
          }),
        );
    }),
    catchError(() => of([] as string[])),
    retry(2),
    tap(() => this.searching$.next(false)),
  );

  ngOnInit(): void {
    this.searchControl.markAsTouched();

    // Subscribe to focus the input element when opened
    this._subscription.add(
      this.searchUiStateService.searchOpened.subscribe(() => {
        setTimeout(() => {
          if (this.searchInput?.nativeElement && this.searchFocus) {
            this.searchInput.nativeElement.focus();
          }
        });
      }),
    );

    this._subscription.add(
      this.searchUiStateService.autocompleteClosed.subscribe(() => {
        if (this.router.url.includes('/ps/')) this.autocompleteTrigger.closePanel();
      }),
    );
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  clearSearch($event: MouseEvent) {
    $event.preventDefault();
    this.searchControl.setValue('');
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.showClearButton$ = this.searchControl.valueChanges.pipe(
      map((value) => value.length > 0),
      startWith(this.searchControl.value.length > 0),
    );
  }
}
